import React, { createContext, useContext, useMemo } from "react";

export interface HealthRefreshContextValue {
    /**
     * Ask the Employees page to re-fetch the Vault77 health report. Call this after any mutation
     * that can resolve (or create) a health finding — a pay-rate change, a sensitive-data save, or a
     * review approval — so the toolbar gauges and the per-row warning icons update live, without a
     * full page reload. No-op when there is no provider above (non-HR users, the notification
     * deep-link path, or shared-detail views that never load the report).
     */
    notifyHealthChanged: () => void;
}

const NOOP = () => {};

export const HealthRefreshContext = createContext<HealthRefreshContextValue>({
    notifyHealthChanged: NOOP,
});

/**
 * Exposes a "the data behind a health finding just changed — re-scan" callback to anything inside the
 * employee detail panel, so deep mutation sites (PayRateModal, UserSecure, PayRatePrompt) can refresh
 * the health report the Employees page holds in state without prop-drilling through five component
 * layers. Read-only consumers should still use ReviewHealthContext; this is purely the write/refresh
 * signal. Degrades to a no-op when `onHealthChanged` is absent.
 */
export const HealthRefreshProvider: React.FC<{
    onHealthChanged?: (() => void) | null;
    children: React.ReactNode;
}> = ({ onHealthChanged, children }) => {
    const value = useMemo<HealthRefreshContextValue>(
        () => ({ notifyHealthChanged: onHealthChanged || NOOP }),
        [onHealthChanged],
    );
    return <HealthRefreshContext.Provider value={value}>{children}</HealthRefreshContext.Provider>;
};

/** Hook form for functional consumers (class consumers use `static contextType = HealthRefreshContext`). */
export function useHealthRefresh(): () => void {
    return useContext(HealthRefreshContext).notifyHealthChanged;
}
